import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Loader2, Send } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { useApplicationMutations, useMyApplications } from "@/hooks/useCollaborations";
import CommissionSplit from "./CommissionSplit";

/**
 * The creator's side of a campaign: apply, or see where their application got to.
 *
 * Until this existed a creator could browse campaigns and do nothing about
 * them, which is the marketplace working in one direction only.
 */
export default function ApplyPanel({
  campaignId,
  currency,
  commissionPct,
  campaignIsOpen,
}: {
  campaignId: string;
  currency: string;
  commissionPct: number;
  campaignIsOpen: boolean;
}) {
  const { t } = useTranslation();
  const { applications, loading } = useMyApplications();
  const { applyToCampaign, withdrawApplication, busy } = useApplicationMutations();

  const [pitch, setPitch] = useState("");
  const [rate, setRate] = useState("");
  const [error, setError] = useState<string | null>(null);

  const existing = applications.find((a) => a.campaign?.id === campaignId);
  const live =
    existing && existing.status !== "WITHDRAWN" && existing.status !== "REJECTED"
      ? existing
      : null;

  const submit = async () => {
    setError(null);
    const proposed = rate.trim() ? Number(rate) : null;
    if (proposed !== null && (!Number.isFinite(proposed) || proposed <= 0)) {
      setError(t("marketplace.apply.invalidRate"));
      return;
    }

    try {
      await applyToCampaign(campaignId, pitch.trim() || undefined, proposed);
      setPitch("");
      setRate("");
    } catch (e) {
      setError(e instanceof Error ? e.message : t("marketplace.apply.failed"));
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-neutral-200 rounded-card p-6 mt-4">
        <p className="text-sm text-neutral-400">{t("common.loading")}</p>
      </div>
    );
  }

  if (live) {
    return (
      <div className="bg-white border border-neutral-200 rounded-card p-6 mt-4">
        <p className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-900">
          <Check className="w-4 h-4 text-emerald-600" />
          {t(`marketplace.apply.applied.${live.status}`)}
        </p>

        {live.status === "ACCEPTED" ? (
          <Link
            to="/colaboraciones"
            className="inline-block mt-3 text-sm font-medium text-steel-600 hover:text-steel-700"
          >
            {t("marketplace.apply.goToCollaboration")}
          </Link>
        ) : (
          <button
            type="button"
            disabled={busy}
            onClick={() => withdrawApplication(campaignId)}
            className="block mt-3 text-xs font-medium text-neutral-400 hover:text-red-600 transition-colors cursor-pointer disabled:opacity-40"
          >
            {t("marketplace.apply.withdraw")}
          </button>
        )}
      </div>
    );
  }

  if (!campaignIsOpen) return null;

  return (
    <div className="bg-white border border-neutral-200 rounded-card p-6 mt-4">
      <h2 className="text-sm font-semibold text-neutral-900">{t("marketplace.apply.title")}</h2>
      <p className="text-xs text-neutral-500 mt-0.5 mb-4">{t("marketplace.apply.subtitle")}</p>

      <div className="space-y-3">
        <textarea
          rows={4}
          value={pitch}
          onChange={(e) => setPitch(e.target.value)}
          placeholder={t("marketplace.apply.pitchPlaceholder")}
          className="w-full px-3 py-2 rounded-control border border-neutral-200 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-steel-600/20 focus:border-steel-600"
        />

        <div className="flex items-center gap-2">
          <span className="text-xs text-neutral-500 flex-shrink-0">{currency}</span>
          <input
            type="number"
            min="0"
            step="1"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            placeholder={t("marketplace.apply.ratePlaceholder")}
            className="flex-1 min-w-0 px-3 py-2 rounded-control border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-steel-600/20 focus:border-steel-600"
          />
        </div>

        {/* What the creator keeps, before they name a number rather than after.
            Commission is charged on top, so this is always the full rate. */}
        {Number(rate) > 0 && (
          <div className="px-3 py-2 rounded-control bg-neutral-50 border border-neutral-100">
            <CommissionSplit
              rate={Number(rate)}
              currency={currency}
              commissionPct={commissionPct}
              viewerParty="CREATOR"
              compact
            />
          </div>
        )}

        {error && (
          <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-control px-3 py-2">
            {error}
          </p>
        )}

        <button
          type="button"
          disabled={busy}
          onClick={submit}
          className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-button bg-gradient-to-r from-steel-600 to-steel-700 text-white text-sm font-medium hover:from-steel-500 hover:to-steel-600 transition-all cursor-pointer disabled:opacity-40"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {t("marketplace.apply.submit")}
        </button>
      </div>
    </div>
  );
}
